<template>
  <CoreDialog
    :value="value"
    data-qa="lpu_promotions_archive_dialog"
    :title="title"
    :size="isMobile ? 'fullscreen' : 'xl'"
    :max-height="isMobile ? undefined : '80vh'"
    divider
    @input="emit('input', $event)"
  >
    <template #prependBtn="{ btnProps }">
      <v-btn
        v-bind="btnProps"
        @click="emit('input', false)"
      >
        <span class="ui-icon-close-not-a-circle" />
      </v-btn>
    </template>

    <div
      class="promotions-archive-dialog"
      :class="{ 'promotions-archive-dialog--mobile': isMobile }"
    >
      <div
        v-if="isLoading"
        class="d-flex align-center justify-center py-10"
      >
        <v-progress-circular
          width="2.5"
          size="42"
          color="primary"
          indeterminate
        />
      </div>
      <div
        v-else-if="html"
        ref="bodyRef"
        class="promotions-archive-dialog__body"
        data-qa="lpu_promotions_archive_body"
        @click="onBodyClick"
        v-html="html/* eslint-disable-line vue/no-v-html */"
      />
    </div>
  </CoreDialog>
</template>

<script lang="ts" setup>
import {
  computed,
  nextTick,
  onMounted,
  ref,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import apiLpuPromotionsArchive from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/api/apiLpuPromotionsArchive'
import resolveArchivePageUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPromotions/functions/resolveArchivePageUrl'

type Emits = {
  (_event: 'input', _value: boolean): void
}

const props = defineProps<{
  value: boolean
  archiveUrl: string
}>()

const emit = defineEmits<Emits>()

const isMobile = useIsMobile()
const html = ref('')
const isLoading = ref(false)
const bodyRef = ref<HTMLElement | null>(null)
const title = computed(() => tStatic('Архивные акции'))

async function loadArchive(url: string) {
  if (!url) {
    return
  }

  isLoading.value = true

  try {
    const nextHtml = await apiLpuPromotionsArchive(url)

    html.value = nextHtml
  } catch {
    html.value = ''
  } finally {
    isLoading.value = false
  }

  await nextTick()

  const bodyEl = bodyRef.value

  if (bodyEl) {
    bodyEl.scrollTop = 0
  }
}

function onBodyClick(event: MouseEvent) {
  const { target } = event

  if (!(target instanceof Element)) {
    return
  }

  const link = target.closest('.b-pagination a')

  if (!(link instanceof HTMLAnchorElement)) {
    return
  }

  event.preventDefault()
  loadArchive(resolveArchivePageUrl(link.getAttribute('href') || '', props.archiveUrl))
}

onMounted(() => {
  loadArchive(props.archiveUrl)
})
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.promotions-archive-dialog {
  &__body {
    :deep(.b-clinic-promotions) {
      margin: 0;
    }

    :deep(.b-clinic-promotions__item) {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 24px 0;
      border-bottom: 1px solid $ui-kit-bg-gray-60;
    }

    :deep(.b-clinic-promotions__title) {
      order: 1;
      margin: 0;
      font-size: $font-size-h6;
      font-weight: $weight-medium;
      line-height: 1.3em;
      color: $ui-kit-text;
    }

    :deep(.b-clinic-promotions__remaining) {
      font-size: $font-size-body-1;
      line-height: 1.25em;
      color: $ui-kit-text-secondary;
    }

    :deep(.b-clinic-promotions__item > .b-clinic-promotions__remaining) {
      order: 2;
    }

    :deep(.b-clinic-promotions__meta) {
      order: 2;
      display: flex;
      justify-content: space-between;
      margin: 0;
    }

    :deep(.b-clinic-promotions__date) {
      font-size: $font-size-body-1;
      line-height: 1.25em;
      color: $ui-kit-text-secondary;
    }

    :deep(.b-clinic-promotions__description) {
      order: 3;
      height: auto;
      min-height: 0;
      padding: 24px;
      margin: 0;
      background: $ui-kit-bg-gray-40;
      border-radius: 20px;
    }

    :deep(.b-clinic-promotions__content) {
      display: block;
      overflow: visible;
      font-size: $font-size-body-1;
      line-height: 1.25em;
      color: $ui-kit-text;
      /* stylelint-disable-next-line property-no-vendor-prefix */
      -webkit-line-clamp: unset;
    }

    :deep(.b-doctor-details__more-btn-container) {
      display: none;
    }

    :deep(.b-clinic-promotions__star-text) {
      order: 4;
      margin: 0;
      font-size: $font-size-body-1;
      line-height: 1.25em;
      color: $ui-kit-text-secondary;
    }

    :deep(.b-clinic-promotions__order) {
      order: 5;
      display: flex;
      align-items: center;
      gap: 24px;
      margin: 0;
      justify-content: flex-start;
    }

    :deep(.b-clinic-promotions__order-text) {
      font-size: $font-size-body-1;
      line-height: 1.25em;
      color: $ui-kit-text-secondary;
    }

    :deep(.b-clinic-promotions__phone) {
      display: flex;
      align-items: center;
      gap: 8px;
      color: $ui-kit-text;
    }

    :deep(.b-clinic-promotions__phone .primary--text),
    :deep(.b-clinic-promotions__phone .b-text-unit_weight_medium) {
      color: $ui-kit-text;
      font-weight: $weight-regular;
    }

    :deep(.b-pagination) {
      display: flex;
      justify-content: center;
      padding: 16px 0;
    }

    :deep(.b-pagination a),
    :deep(.b-pagination .current) {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      margin: 0 4px;
      border-radius: $border-radius-sm;
      text-decoration: none;
    }

    :deep(.b-pagination a) {
      color: var(--lpu-branding-color-primary, $ui-kit-primary);
      border: 1px solid var(--lpu-branding-color-primary, $ui-kit-primary);
    }

    :deep(.b-pagination .current) {
      color: $ui-kit-text-secondary;
      border: 1px solid $ui-kit-text-secondary;
    }
  }

  &--mobile &__body {
    :deep(.b-clinic-promotions__item) {
      padding: 16px 0;
    }

    :deep(.b-clinic-promotions__title) {
      font-size: $font-size-subtitle-1;
      line-height: 1.35em;
    }

    :deep(.b-clinic-promotions__description) {
      padding: 16px;
    }

    :deep(.b-clinic-promotions__star-text) {
      font-size: $font-size-body-2;
      line-height: 1.4em;
    }

    :deep(.b-clinic-promotions__order) {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
  }
}
</style>
